import { useEffect, useRef } from 'react';
import { prefersReducedMotion } from './hooks';

export default function Field() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const reduce = prefersReducedMotion();
    const mouse = { x: -9999, y: -9999 };
    const nodes = [];
    let frame = 0;
    let onScreen = true;
    let visible = document.visibilityState !== 'hidden';
    let width = 0;
    let height = 0;
    let rect = canvas.getBoundingClientRect();
    let link = '110, 231, 255';
    let node = '238, 243, 255';
    let last = 0;

    const readColors = () => {
      const styles = getComputedStyle(document.documentElement);
      link = styles.getPropertyValue('--canvas').trim() || link;
      node = styles.getPropertyValue('--canvas-node').trim() || node;
    };

    const paint = () => {
      const reach = width < 700 ? 90 : 120;
      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 1;

      for (let i = 0; i < nodes.length; i += 1) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j += 1) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < reach) {
            ctx.strokeStyle = `rgba(${link}, ${((1 - dist / reach) * 0.35).toFixed(3)})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      ctx.fillStyle = `rgba(${node}, 0.85)`;
      nodes.forEach((point) => {
        ctx.beginPath();
        ctx.arc(point.x, point.y, point.r, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    const step = (now) => {
      if (!onScreen || !visible) return;
      if (now - last < 40) {
        frame = requestAnimationFrame(step);
        return;
      }
      last = now;
      if (!reduce) {
        nodes.forEach((point) => {
          if (mouse.x > -1000) {
            const dx = mouse.x - point.x;
            const dy = mouse.y - point.y;
            const dist = Math.hypot(dx, dy) || 1;
            if (dist < 140) {
              point.vx += (dx / dist) * 0.02;
              point.vy += (dy / dist) * 0.02;
            }
          }
          point.x += point.vx;
          point.y += point.vy;
          if (point.x < 0 || point.x > width) point.vx *= -1;
          if (point.y < 0 || point.y > height) point.vy *= -1;
          point.vx *= 0.99;
          point.vy *= 0.99;
        });
      }
      paint();
      if (!reduce) frame = requestAnimationFrame(step);
    };

    const seed = () => {
      rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      if (width < 2 || height < 2) return false;
      canvas.width = Math.max(1, Math.floor(width));
      canvas.height = Math.max(1, Math.floor(height));
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const count = width < 700 ? 14 : 26;
      nodes.length = 0;
      for (let i = 0; i < count; i += 1) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.35,
          vy: (Math.random() - 0.5) * 0.35,
          r: Math.random() * 1.2 + 0.6,
        });
      }
      return true;
    };

    const restart = () => {
      cancelAnimationFrame(frame);
      if (onScreen && visible && !reduce) frame = requestAnimationFrame(step);
      else paint();
    };

    const onResize = () => {
      if (seed() === false) return;
      restart();
    };

    const onMove = (event) => {
      if (!onScreen) return;
      mouse.x = event.clientX - rect.left;
      mouse.y = event.clientY - rect.top;
    };

    let scrollFrame = 0;
    const onScroll = () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = 0;
        rect = canvas.getBoundingClientRect();
      });
    };

    readColors();
    if (seed() !== false) restart();
    else requestAnimationFrame(onResize);

    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (!onScreen) mouse.x = -9999;
      restart();
    });
    observer.observe(canvas);
    const onVisibility = () => {
      visible = document.visibilityState !== 'hidden';
      restart();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);

    const theme = new MutationObserver(() => {
      readColors();
      paint();
    });
    theme.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(scrollFrame);
      observer.disconnect();
      theme.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={ref} className="field" aria-hidden="true" />;
}
